// Workers: overview, fleet, queues, tasks, activity, schedule, failures, issues and audit
// over the workers admin API.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Cpu,
  Eye,
  Inbox,
  ListChecks,
  Play,
  RefreshCw,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  useWorkersAuditQuery,
  useWorkersFailureGroupQuery,
  useWorkersFailuresQuery,
  useWorkersFleetQuery,
  useWorkersIssuesQuery,
  useWorkersOverviewQuery,
  useWorkersQueuesQuery,
  useWorkersRefresh,
  useWorkersRunsQuery,
  useWorkersScheduleQuery,
  useWorkersTasksQuery,
} from '@/client/api/workers/workers.query';
import {
  FailureGroup,
  ScheduleEntry,
  TaskRow,
  Worker,
  WorkerEnv,
  WorkerWindow,
} from '@/client/types/workers';
import {
  Card,
  EmptyRow,
  HealthDot,
  HealthPill,
  HealthReasons,
  INPUT_CLASS,
  MetricChart,
  PageShell,
  StatCard,
  Tabs,
  Th,
  UsagePct,
  fmtAgo,
  fmtNum,
  fmtTime,
} from '../Cloud/cloudUi';
import {
  AccessDenied,
  AuditTable,
  ENVS,
  ErrorNotice,
  FailRate,
  FailureGroupRow,
  FailureList,
  FetchingChip,
  HeldTable,
  IssueRow,
  LoadingNote,
  QueueLink,
  RecordingNote,
  RowMenu,
  RunsTable,
  Spark,
  TaskLink,
  Throughput,
  WINDOWS,
  WORKERS_BASE,
  WorkerLink,
  describeSchedule,
  fmtFailRate,
  fmtMs,
  fmtSpan,
  queueItems,
  seriesOf,
  shortWorker,
  tabPath,
  taskItems,
  taskPath,
  useWorkerEnv,
  useWorkerPerms,
  workerItems,
} from './workersUi';

type Tab =
  | 'overview'
  | 'fleet'
  | 'queues'
  | 'tasks'
  | 'activity'
  | 'schedule'
  | 'failures'
  | 'issues'
  | 'audit';
const TABS: Tab[] = [
  'overview',
  'fleet',
  'queues',
  'tasks',
  'activity',
  'schedule',
  'failures',
  'issues',
  'audit',
];
const WINDOW_HOURS: Record<WorkerWindow, number> = { '6h': 6, '24h': 24, '7d': 168, '30d': 720 };

type Perms = ReturnType<typeof useWorkerPerms>;
type TabProps = { env: WorkerEnv; window: WorkerWindow; perms: Perms };

const Frame: React.FC<{
  q: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  loading?: React.ReactNode;
  children: React.ReactNode;
}> = ({ q, loading, children }) => (
  <>
    {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
    {q.isLoading ? (
      <>
        <LoadingNote>{loading}</LoadingNote>
        <Skeleton className="h-48 w-full" />
      </>
    ) : (
      children
    )}
  </>
);

const Slots: React.FC<{ w: Worker }> = ({ w }) =>
  w.alive ? (
    <span className="text-gray-900">
      {w.active ?? 0}
      <span className="text-gray-400"> of {w.concurrency ?? '?'}</span>
    </span>
  ) : (
    <span className="text-red-700">not answering</span>
  );

/* ---------- overview ---------- */

const WorkerCard: React.FC<{ w: Worker; env: WorkerEnv; perms: Perms }> = ({ w, env, perms }) => {
  const isBeat = w.role === 'beat';
  const to = isBeat ? tabPath('schedule', env) : `${WORKERS_BASE}/fleet/${encodeURIComponent(w.name)}${env === 'staging' ? '?env=staging' : ''}`;
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={to} className="font-medium text-gray-900 hover:underline">
            {isBeat ? 'Scheduler (beat)' : shortWorker(w.name)}
          </Link>
          <p className="truncate text-xs text-gray-500">
            {w.component ?? 'not in the deployed spec'}
            {w.size ? ` · ${w.size}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <HealthPill level={w.health.level} />
          <RowMenu name={shortWorker(w.name)} env={env} items={isBeat ? [{ label: 'View schedule', icon: <Eye className="h-4 w-4" />, to }, ...workerItems(w, env, perms, false)] : workerItems(w, env, perms)} />
        </div>
      </div>
      <HealthReasons health={w.health} className="mt-2" />
      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
        {isBeat ? (
          <>
            <div>
              <dt className="text-gray-500">Entries</dt>
              <dd className="text-sm text-gray-900">{fmtNum(w.entries)}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-gray-500">Last dispatch</dt>
              <dd className="text-sm text-gray-900">{fmtAgo(w.last_dispatch_at)}</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt className="text-gray-500">Running</dt>
              <dd className="text-sm">
                <Slots w={w} />
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Waiting for it</dt>
              <dd className="text-sm text-gray-900">{fmtNum(w.backlog)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Runs, last hour</dt>
              <dd className="text-sm text-gray-900">
                {fmtNum(w.hour?.runs)}
                {!!w.hour?.failed && <span className="text-red-700"> · {w.hour.failed} failed</span>}
              </dd>
            </div>
          </>
        )}
        {w.metrics && (
          <>
            <div>
              <dt className="text-gray-500">Memory</dt>
              <dd className="text-sm">
                <UsagePct pct={w.metrics.memory_pct} />
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">CPU</dt>
              <dd className="text-sm">
                <UsagePct pct={w.metrics.cpu_pct} />
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Restarts, last hour</dt>
              <dd className={`text-sm ${w.metrics.restarts ? 'font-medium text-red-700' : 'text-gray-900'}`}>
                {w.metrics.restarts}
              </dd>
            </div>
          </>
        )}
      </dl>
    </div>
  );
};

const TopList: React.FC<{
  title: string;
  rows: TaskRow[];
  env: WorkerEnv;
  value: (t: TaskRow) => string;
  empty: string;
}> = ({ title, rows, env, value, empty }) => (
  <Card title={title} flush>
    {rows.length === 0 ? (
      <p className="px-4 py-6 text-center text-sm text-gray-400">{empty}</p>
    ) : (
      <ul className="divide-y divide-gray-50">
        {rows.map((t) => (
          <li key={t.name} className="flex items-center justify-between gap-3 px-4 py-2">
            <TaskLink name={t.name} env={env} />
            <span className="whitespace-nowrap text-sm text-gray-700">{value(t)}</span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const OverviewTab: React.FC<TabProps> = ({ env, window, perms }) => {
  const q = useWorkersOverviewQuery(env, window);
  const d = q.data;
  const answering = d?.workers.filter((w) => w.alive).length ?? 0;
  return (
    <Frame q={q}>
      {d && (
        <>
          <RecordingNote since={d.recording_since} env={env} />
          {!d.provider_known && (
            <p className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              The cloud provider could not be read, so this shows the workers that answer, not the
              ones that should. A worker that is down is missing from the list rather than red.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatCard
              label="Workers answering"
              value={`${answering} of ${d.workers.length}`}
              hint={d.beat ? `scheduler ${d.beat.alive ? 'running' : 'not running'}` : 'no scheduler here'}
              icon={<Cpu className="h-5 w-5 text-blue-600" />}
              color="bg-blue-50"
            />
            <StatCard
              label="Running now"
              value={`${d.busy.active} of ${d.busy.slots} slots`}
              hint={`${d.busy.reserved} held, ${d.busy.scheduled} waiting for their time`}
              icon={<Activity className="h-5 w-5 text-teal-600" />}
              color="bg-teal-50"
            />
            <StatCard
              label="Waiting in queues"
              value={fmtNum(d.backlog.now)}
              hint={d.backlog.oldest_age ? `oldest ${fmtSpan(d.backlog.oldest_age)}` : 'nothing waiting long'}
              icon={<Inbox className="h-5 w-5 text-violet-600" />}
              color="bg-violet-50"
            />
            <StatCard
              label={`Runs, last ${window}`}
              value={fmtNum(d.totals.runs)}
              hint={`${fmtFailRate(d.totals)} failed · avg ${fmtMs(d.totals.runtime_avg_ms)}`}
              icon={<ListChecks className="h-5 w-5 text-green-600" />}
              color="bg-green-50"
            />
            <StatCard
              label="Open issues"
              value={fmtNum(d.issues.open)}
              hint={`${d.issues.red} critical · ${d.failures.open} failure groups`}
              icon={<AlertTriangle className="h-5 w-5 text-amber-600" />}
              color="bg-amber-50"
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {d.workers.map((w) => (
              <WorkerCard key={w.name} w={w} env={env} perms={perms} />
            ))}
            {d.beat && <WorkerCard w={d.beat} env={env} perms={perms} />}
          </div>

          {(d.issues.rows.length > 0 || d.failures.rows.length > 0) && (
            <Card
              title="Needs attention"
              action={
                <Link to={tabPath('issues', env)} className="text-xs text-blue-600 hover:underline">
                  All issues
                </Link>
              }
            >
              <div className="space-y-3">
                {d.issues.rows.map((i) => (
                  <IssueRow key={i.id} issue={i} canOperate={perms.canOperate} />
                ))}
                {d.failures.rows.map((g) => (
                  <FailureGroupRow key={g.sig} group={g} env={env} canOperate={perms.canOperate} />
                ))}
              </div>
            </Card>
          )}

          <div className="grid gap-3 xl:grid-cols-2">
            <Card title="Runs and failures" note={`last ${window}`}>
              <Throughput series={d.series} window={window} />
            </Card>
            <Card title="Average run time and queue wait" note="milliseconds">
              <MetricChart
                series={[
                  ...seriesOf(d.series, 'runtime_ms', 'Run time'),
                  ...seriesOf(d.series, 'wait_ms', 'Queue wait'),
                ]}
                spanHours={WINDOW_HOURS[window]}
              />
            </Card>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            <Card
              title="Waiting in queues"
              note={env === 'production' ? 'last 24 hours, all queues' : undefined}
              action={
                <Link to={tabPath('queues', env)} className="text-xs text-blue-600 hover:underline">
                  All queues
                </Link>
              }
            >
              {env === 'production' ? (
                <MetricChart
                  series={[{ component: 'all', instance: 'Messages waiting', points: d.backlog.series }]}
                  spanHours={24}
                />
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">
                  Depth over time is sampled on the production broker only. Staging shows the
                  current depth.
                </p>
              )}
              {d.backlog.queues.length > 0 && (
                <ul className="mt-3 divide-y divide-gray-50 text-sm">
                  {d.backlog.queues.map((qu) => (
                    <li key={qu.name} className="flex items-center justify-between py-1.5">
                      <QueueLink name={qu.name} env={env} />
                      <span className="text-gray-700">
                        {fmtNum(qu.depth)} waiting
                        {qu.oldest_age ? `, oldest ${fmtSpan(qu.oldest_age)}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <div className="grid gap-3 sm:grid-cols-2">
              <TopList title="Busiest tasks" rows={d.top.busiest} env={env} value={(t) => `${fmtNum(t.runs)} runs`} empty="No runs recorded." />
              <TopList title="Failing most" rows={d.top.failing} env={env} value={(t) => `${fmtNum(t.failed)} failed`} empty="Nothing failed." />
              <TopList title="Slowest on average" rows={d.top.slowest} env={env} value={(t) => fmtMs(t.runtime_avg_ms)} empty="No runtimes recorded." />
              <TopList title="Waiting longest in a queue" rows={d.top.waiting} env={env} value={(t) => fmtMs(t.wait_avg_ms)} empty="No waits recorded." />
            </div>
          </div>
        </>
      )}
    </Frame>
  );
};

/* ---------- fleet ---------- */

const FleetTab: React.FC<TabProps> = ({ env, perms }) => {
  const q = useWorkersFleetQuery(env);
  const d = q.data;
  const rows = d ? [...d.workers, ...(d.beat ? [d.beat] : [])] : [];
  return (
    <Frame q={q}>
      <Card title="Workers" note={d?.app ? `deployed as ${d.app.name}` : undefined} flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
              <tr>
                <Th>Health</Th>
                <Th>Worker</Th>
                <Th right>Running</Th>
                <Th right>Held</Th>
                <Th>Queues</Th>
                <Th right>Waiting</Th>
                <Th right>Memory</Th>
                <Th right>CPU</Th>
                <Th right>Runs, 1 h</Th>
                <Th right>Up for</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={11}>No worker is deployed or answering on {env}.</EmptyRow>}
              {rows.map((w) => (
                <tr key={w.name} className="border-b border-gray-50 align-top last:border-0">
                  <td className="px-4 py-2">
                    <HealthPill level={w.health.level} />
                    <HealthReasons health={w.health} className="mt-1 max-w-xs" />
                  </td>
                  <td className="px-4 py-2">
                    {w.role === 'beat' ? (
                      <Link to={tabPath('schedule', env)} className="font-medium text-gray-900 hover:underline">
                        Scheduler (beat)
                      </Link>
                    ) : (
                      <WorkerLink name={w.name} env={env} />
                    )}
                    <span className="block text-xs text-gray-500">
                      {[w.component, w.size, w.branch && `branch ${w.branch}`].filter(Boolean).join(' · ') ||
                        'not in the deployed spec'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{w.role === 'worker' ? <Slots w={w} /> : ''}</td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {w.role === 'worker' && w.alive ? (w.reserved ?? 0) + (w.scheduled ?? 0) : ''}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {w.role === 'worker'
                      ? `${(w.queues?.length || w.expected_queues?.length) ?? 0} queues`
                      : `${fmtNum(w.entries)} entries, last dispatch ${fmtAgo(w.last_dispatch_at)}`}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{w.role === 'worker' ? fmtNum(w.backlog) : ''}</td>
                  <td className="px-4 py-2 text-right">{w.metrics && <UsagePct pct={w.metrics.memory_pct} />}</td>
                  <td className="px-4 py-2 text-right">{w.metrics && <UsagePct pct={w.metrics.cpu_pct} />}</td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {w.hour ? (
                      <>
                        {fmtNum(w.hour.runs)}
                        {!!w.hour.failed && <span className="text-red-700"> · {w.hour.failed} failed</span>}
                      </>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500">{w.uptime != null ? fmtSpan(w.uptime) : ''}</td>
                  <td className="w-12 px-4 py-2 text-right">
                    <RowMenu
                      name={shortWorker(w.name)}
                      env={env}
                      items={
                        w.role === 'beat'
                          ? [{ label: 'View schedule', icon: <Eye className="h-4 w-4" />, to: tabPath('schedule', env) }, ...workerItems(w, env, perms, false)]
                          : workerItems(w, env, perms)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="Held by workers right now" note="refreshes every 15 seconds" flush>
        <HeldTable rows={d?.tasks ?? []} env={env} canOperate={perms.canOperate} />
      </Card>
    </Frame>
  );
};

/* ---------- queues ---------- */

const QueuesTab: React.FC<TabProps> = ({ env, perms }) => {
  const q = useWorkersQueuesQuery(env);
  const rows = q.data?.queues ?? [];
  return (
    <Frame q={q}>
      <Card
        title="Queues"
        note={q.data?.trend_available ? 'trend is the last 24 hours' : 'depth over time is sampled on production only'}
        flush
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
              <tr>
                <Th>Health</Th>
                <Th>Queue</Th>
                <Th right>Waiting</Th>
                <Th>At the front</Th>
                <Th>Consumed by</Th>
                <Th right>Runs, 1 h</Th>
                <Th right>Avg wait, 1 h</Th>
                {q.data?.trend_available && <Th>Depth</Th>}
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-b border-gray-50 align-top last:border-0">
                  <td className="px-4 py-2">
                    <HealthPill level={r.health.level} />
                    <HealthReasons health={r.health} className="mt-1 max-w-xs" />
                  </td>
                  <td className="px-4 py-2">
                    <QueueLink name={r.name} env={env} />
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmtNum(r.depth)}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {r.oldest ? (
                      <>
                        <Link to={taskPath(r.oldest.task ?? '', env)} className="hover:underline">
                          {r.oldest.task?.split('.').pop() ?? 'unreadable message'}
                        </Link>
                        {r.oldest_age != null && <span className="block text-gray-500">waiting {fmtSpan(r.oldest_age)}</span>}
                      </>
                    ) : (
                      'empty'
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.consumers.length
                      ? r.consumers.map((c) => (
                          <span key={c} className="mr-2">
                            <WorkerLink name={c} env={env} />
                          </span>
                        ))
                      : <span className={r.depth ? 'font-medium text-red-700' : 'text-gray-500'}>no worker</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtNum(r.hour.runs)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.hour.wait_avg_ms)}</td>
                  {q.data?.trend_available && (
                    <td className="px-4 py-2">
                      <Spark points={r.trend} label={`${r.name} depth`} />
                    </td>
                  )}
                  <td className="w-12 px-4 py-2 text-right">
                    <RowMenu name={r.name} env={env} items={queueItems(r.name, env, perms, r.depth)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Frame>
  );
};

/* ---------- tasks ---------- */

type TaskFilter = 'all' | 'ran' | 'problems' | 'scheduled' | 'idle';
type TaskSort = 'runs' | 'failed' | 'runtime_avg_ms' | 'wait_avg_ms' | 'name';

const TasksTab: React.FC<TabProps> = ({ env, window, perms }) => {
  const q = useWorkersTasksQuery(env, window);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<TaskFilter>('all');
  const [sort, setSort] = useState<TaskSort>('runs');
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (q.data?.tasks ?? [])
      .filter((t) => !term || t.name.toLowerCase().includes(term) || (t.queue ?? '').includes(term))
      .filter(
        (t) =>
          filter === 'all' ||
          (filter === 'ran' && t.runs > 0) ||
          (filter === 'problems' && t.health.level !== 'green') ||
          (filter === 'scheduled' && t.scheduled.length > 0) ||
          (filter === 'idle' && t.runs === 0),
      )
      .sort((a, b) =>
        sort === 'name' ? a.name.localeCompare(b.name) : (b[sort] ?? 0) - (a[sort] ?? 0) || a.name.localeCompare(b.name),
      );
  }, [q.data, search, filter, sort]);
  return (
    <Frame q={q} loading="Reading the task catalogue and asking each worker which tasks it knows.">
      <RecordingNote since={q.data?.recording_since} env={env} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Task or queue"
            aria-label="Search tasks"
            className={`${INPUT_CLASS} w-72 pl-8`}
          />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value as TaskFilter)} aria-label="Show" className={INPUT_CLASS}>
          <option value="all">All tasks</option>
          <option value="ran">Ran in this window</option>
          <option value="problems">With a problem</option>
          <option value="scheduled">On the schedule</option>
          <option value="idle">Did not run</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as TaskSort)} aria-label="Sort by" className={INPUT_CLASS}>
          <option value="runs">Most runs</option>
          <option value="failed">Most failures</option>
          <option value="runtime_avg_ms">Slowest</option>
          <option value="wait_avg_ms">Longest wait</option>
          <option value="name">Name</option>
        </select>
        <span className="text-xs text-gray-500">
          {rows.length} of {q.data?.count ?? 0}
        </span>
      </div>
      <Card title="Tasks" note={`figures for the last ${window}`} flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
              <tr>
                <Th>Task</Th>
                <Th>Queue</Th>
                <Th right>Runs</Th>
                <Th right>Failed</Th>
                <Th right>Avg run</Th>
                <Th right>Longest</Th>
                <Th right>Avg wait</Th>
                <Th right>Last ran</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={9}>No task matches.</EmptyRow>}
              {rows.map((t) => (
                <tr key={t.name} className="border-b border-gray-50 align-top last:border-0">
                  <td className="px-4 py-2">
                    <div className="flex items-start gap-2">
                      {t.health.level !== 'green' && <HealthDot level={t.health.level} className="mt-1.5" />}
                      <div className="min-w-0">
                        <TaskLink name={t.name} env={env} />
                        <HealthReasons health={t.health} />
                        {t.scheduled.length > 0 && (
                          <span className="text-[11px] text-violet-700">scheduled: {t.scheduled.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <QueueLink name={t.queue} env={env} />
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">{fmtNum(t.runs)}</td>
                  <td className="px-4 py-2 text-right">
                    <FailRate f={t} />
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtMs(t.runtime_avg_ms)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtMs(t.runtime_max_ms)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtMs(t.wait_avg_ms)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-gray-500">{fmtAgo(t.last_at)}</td>
                  <td className="w-12 px-4 py-2 text-right">
                    <RowMenu name={t.name.split('.').pop()!} env={env} items={taskItems(t.name, env, perms, t.runnable)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Frame>
  );
};

/* ---------- activity ---------- */

const ActivityTab: React.FC<TabProps> = ({ env, perms }) => {
  const [status, setStatus] = useState('');
  const [task, setTask] = useState('');
  const [applied, setApplied] = useState('');
  const q = useWorkersRunsQuery(env, { status, task: applied });
  return (
    <Frame q={q}>
      <RecordingNote since={q.data?.recording_since} env={env} />
      <Card title="Held by workers right now" note="refreshes every 15 seconds" flush>
        <HeldTable rows={q.data?.live ?? []} env={env} canOperate={perms.canOperate} />
      </Card>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(task.trim());
        }}
      >
        <input
          value={task}
          onChange={(e) => setTask(e.target.value)}
          placeholder="Full task name, e.g. tasks.billing_tasks.dunning_retry"
          aria-label="Task name"
          className={`${INPUT_CLASS} w-96 font-mono`}
        />
        <Button type="submit" size="sm" variant="outline">
          Filter
        </Button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Outcome" className={INPUT_CLASS}>
          <option value="">Any outcome</option>
          <option value="SUCCESS">Succeeded</option>
          <option value="FAILURE">Failed</option>
          <option value="RETRY">Retrying</option>
        </select>
      </form>
      <Card title="Finished runs" note="newest first, kept for about a day" flush>
        <RunsTable rows={q.data?.runs ?? []} env={env} />
      </Card>
    </Frame>
  );
};

/* ---------- schedule ---------- */

const EntryRow: React.FC<{ e: ScheduleEntry; env: WorkerEnv; perms: Perms; live: boolean }> = ({
  e,
  env,
  perms,
  live,
}) => (
  <tr className="border-b border-gray-50 align-top last:border-0">
    <td className="px-4 py-2">
      <div className="flex items-start gap-2">
        {e.health.level !== 'green' && <HealthDot level={e.health.level} className="mt-1.5" />}
        <div className="min-w-0">
          <span className="font-medium text-gray-900">{e.name}</span>
          <HealthReasons health={e.health} />
        </div>
      </div>
    </td>
    <td className="px-4 py-2">{e.task ? <TaskLink name={e.task} env={env} /> : ''}</td>
    <td className="whitespace-nowrap px-4 py-2 text-gray-700">{describeSchedule(e.schedule)}</td>
    <td className="px-4 py-2">
      <QueueLink name={e.queue} env={env} />
    </td>
    {live && (
      <>
        <td className="whitespace-nowrap px-4 py-2 text-right text-gray-700">
          {e.last_run_at ? fmtAgo(e.last_run_at) : 'never'}
        </td>
        <td className="whitespace-nowrap px-4 py-2 text-right text-gray-700">{e.due_at ? fmtTime(e.due_at) : ''}</td>
        <td className="px-4 py-2 text-right text-gray-700">{fmtNum(e.run_count)}</td>
      </>
    )}
    <td className="px-4 py-2 text-right text-gray-700">
      {e.day?.runs ? (
        <>
          {fmtNum(e.day.runs)}
          {!!e.day.failed && <span className="text-red-700"> · {e.day.failed} failed</span>}
        </>
      ) : (
        ''
      )}
    </td>
    <td className="w-12 px-4 py-2 text-right">
      <RowMenu
        name={e.name}
        env={env}
        items={[
          ...(e.task ? [{ label: 'View task', icon: <Eye className="h-4 w-4" />, to: taskPath(e.task, env) }] : []),
          ...(perms.canOperate && !e.orphan
            ? [{ label: 'Dispatch now', icon: <Play className="h-4 w-4" />, change: { kind: 'run_entry' as const, entry: e.name } }]
            : []),
        ]}
      />
    </td>
  </tr>
);

const ScheduleTab: React.FC<TabProps> = ({ env, perms }) => {
  const q = useWorkersScheduleQuery(env);
  const [search, setSearch] = useState('');
  const [problems, setProblems] = useState(false);
  const d = q.data;
  const rows = (d?.entries ?? []).filter(
    (e) =>
      (!problems || e.health.level !== 'green') &&
      (!search.trim() || `${e.name} ${e.task ?? ''}`.toLowerCase().includes(search.trim().toLowerCase())),
  );
  const held = (d?.locks ?? []).filter((l) => l.held);
  return (
    <Frame q={q} loading="Reading the schedule and what the scheduler last did with each entry.">
      {d && (
        <>
          {d.runs_beat ? (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${
                d.leader.held ? 'border-green-100 bg-green-50 text-green-900' : 'border-red-100 bg-red-50 text-red-800'
              }`}
            >
              {d.leader.held
                ? `A scheduler holds the leader lock and last dispatched ${fmtAgo(d.last_dispatch_at)}.`
                : 'No scheduler holds the leader lock. Nothing on this schedule is being dispatched.'}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
              {env === 'staging' ? 'Staging runs no scheduler by design' : 'No scheduler has written to this broker'}
              : nothing here fires on its own. Dispatch an entry by hand to exercise it.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Entry or task"
                aria-label="Search schedule"
                className={`${INPUT_CLASS} w-72 pl-8`}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} />
              Only entries with a problem
            </label>
            <span className="text-xs text-gray-500">
              {rows.length} of {d.entries.length}
            </span>
          </div>
          <Card title="Periodic tasks" flush>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
                  <tr>
                    <Th>Entry</Th>
                    <Th>Task</Th>
                    <Th>When</Th>
                    <Th>Queue</Th>
                    {d.runs_beat && (
                      <>
                        <Th right>Last dispatched</Th>
                        <Th right>Next due</Th>
                        <Th right>Dispatches</Th>
                      </>
                    )}
                    <Th right>Runs, 24 h</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <EmptyRow cols={d.runs_beat ? 9 : 6}>No entry matches.</EmptyRow>}
                  {rows.map((e) => (
                    <EntryRow key={e.name} e={e} env={env} perms={perms} live={d.runs_beat} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {d.locks && (
            <Card
              title="Run-one-at-a-time locks"
              note={`${held.length} of ${d.locks.length} held`}
            >
              <p className="mb-2 text-sm text-gray-600">
                A task that holds one of these is running somewhere. A lock that stays held past its
                task is a worker that died mid-run; it frees itself when its timeout ends. They
                cannot be released from here: that is how a renewal gets charged twice.
              </p>
              {held.length === 0 ? (
                <p className="text-sm text-gray-500">None held right now.</p>
              ) : (
                <ul className="divide-y divide-gray-50 text-sm">
                  {held.map((l) => (
                    <li key={l.name} className="flex justify-between py-1.5">
                      <span className="font-mono text-xs text-gray-800">{l.name}</span>
                      <span className="text-gray-600">frees itself in {fmtSpan(l.ttl)} at the latest</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </>
      )}
    </Frame>
  );
};

/* ---------- failures ---------- */

const GroupRuns: React.FC<{ env: WorkerEnv; window: WorkerWindow; sig: string; canOperate: boolean }> = ({
  env,
  window,
  sig,
  canOperate,
}) => {
  const q = useWorkersFailureGroupQuery(env, window, sig);
  return (
    <div className="border-t border-gray-100">
      {q.isLoading ? (
        <Skeleton className="m-4 h-16" />
      ) : (
        <FailureList rows={q.data?.failures ?? []} env={env} canOperate={canOperate} showTask={false} />
      )}
    </div>
  );
};

const FailuresTab: React.FC<TabProps> = ({ env, window, perms }) => {
  const [status, setStatus] = useState<'open' | 'acknowledged' | 'all'>('open');
  const [open, setOpen] = useState<string | null>(null);
  const q = useWorkersFailuresQuery(env, window, status);
  const rows: FailureGroup[] = q.data?.groups ?? [];
  return (
    <Frame q={q} loading="Grouping failed runs.">
      <RecordingNote since={q.data?.recording_since} env={env} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Failed runs in the last {window}, grouped when they are the same failure. Failures are
          kept for 30 days.
        </p>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Status" className={INPUT_CLASS}>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="all">All</option>
        </select>
      </div>
      {rows.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          No {status === 'all' ? '' : status} failures in this window.
        </div>
      )}
      <div className="space-y-3">
        {rows.map((g) => (
          <FailureGroupRow
            key={g.sig}
            group={g}
            env={env}
            canOperate={perms.canOperate}
            open={open === g.sig}
            onOpen={() => setOpen(open === g.sig ? null : g.sig)}
          >
            {open === g.sig && <GroupRuns env={env} window={window} sig={g.sig} canOperate={perms.canOperate} />}
          </FailureGroupRow>
        ))}
      </div>
    </Frame>
  );
};

/* ---------- issues and audit ---------- */

const IssuesTab: React.FC<TabProps> = ({ env, perms }) => {
  const [status, setStatus] = useState<'open' | 'acknowledged' | 'all'>('open');
  const q = useWorkersIssuesQuery(env, status);
  const rows = q.data?.issues ?? [];
  return (
    <Frame q={q}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Every reason a worker, a queue or the scheduler is red or yellow. An issue clears by
          itself when the cause does.
        </p>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Status" className={INPUT_CLASS}>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="all">All</option>
        </select>
      </div>
      {rows.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          No {status === 'all' ? '' : status} issues.
        </div>
      )}
      <div className="space-y-3">
        {rows.map((i) => (
          <IssueRow key={i.id} issue={i} canOperate={perms.canOperate} />
        ))}
      </div>
    </Frame>
  );
};

const AuditTab: React.FC<TabProps> = ({ env }) => {
  const q = useWorkersAuditQuery(env);
  return (
    <Frame q={q} loading="Reading what the console changed.">
      <Card title="What the console changed" note={env} flush>
        <AuditTable rows={q.data?.audit ?? []} empty="Nothing has been changed from the console yet." />
      </Card>
    </Frame>
  );
};

/* ---------- page ---------- */

const TAB_VIEW: Record<Tab, React.FC<TabProps>> = {
  overview: OverviewTab,
  fleet: FleetTab,
  queues: QueuesTab,
  tasks: TasksTab,
  activity: ActivityTab,
  schedule: ScheduleTab,
  failures: FailuresTab,
  issues: IssuesTab,
  audit: AuditTab,
};
const WINDOWED: Tab[] = ['overview', 'tasks', 'failures'];

const WorkersPage: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'overview' }) => {
  useBreadcrumbs([
    { label: 'Workers', href: WORKERS_BASE },
    ...(initialTab === 'overview'
      ? []
      : [{ label: initialTab.charAt(0).toUpperCase() + initialTab.slice(1) }]),
  ]);
  const perms = useWorkerPerms();
  const navigate = useNavigate();
  const [env, setEnv] = useWorkerEnv();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [window, setWindow] = useState<WorkerWindow>('24h');
  useEffect(() => setTab(initialTab), [initialTab]);
  const refresh = useWorkersRefresh();
  const View = TAB_VIEW[tab];

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Workers" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }
  return (
    <PageShell
      title="Workers"
      description="The background workers deployed with ff-api: which are up, what each is running, what is waiting in which queue, how long work takes and what fails."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <FetchingChip />
          <Tabs value={env} onChange={setEnv} options={ENVS} />
          {WINDOWED.includes(tab) && <Tabs value={window} onChange={setWindow} options={WINDOWS} />}
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 overflow-x-auto border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => navigate(tabPath(t, env))}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize ${
              tab === t
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <View env={env} window={window} perms={perms} />
    </PageShell>
  );
};

export default WorkersPage;
